import { useEffect, useRef, useState } from 'react'
import {
  DURACAO_META_MS,
  DURACAO_METADE_MS,
  DURACAO_MS,
  gravarMetaCelebrada,
  gravarMetadeCelebrada,
  gravarUltimoMarco,
  lerMetaCelebrada,
  lerMetadeCelebrada,
  lerUltimoMarco,
  marcoACelebrar,
  marcoDe,
  metaBatida,
  metadeBatida,
} from '../marcos'

/** O que está em cartaz: o marco de milhão, e se ele é a METADE ou a META. */
export type EmCartaz = { marco: number; meta: boolean; metade: boolean }

/**
 * Dispara a comemoração quando o total geral cruza um milhão — e a especial
 * quando ele cruza a META.
 *
 * Quatro coisas que este gancho resolve, e nenhuma delas é obvia:
 *
 *  - **a primeira leitura NÃO comemora.** Ela só registra onde o total está.
 *    Sem isso, abrir o telão com R$ 5,4 mi na tela jogaria confete no primeiro
 *    segundo por um marco batido antes de alguém abrir a página.
 *  - **o que já foi comemorado fica em `localStorage`.** O telão passa dias
 *    ligado e a página é recarregada (deploy, F5 de quem opera, reconexão do
 *    SSE): sem memória em disco, cada recarga comemoraria o mesmo milhão outra
 *    vez, e a sala aprende a ignorar o confete.
 *  - **a META vence a METADE e o milhão quando caem juntos.** Com meta de
 *    R$ 28 mi exatos, cruzar 28 mi é as duas coisas, e o telão mostra uma
 *    comemoração — a da meta —, não duas em fila. Bater a meta também marca a
 *    metade como celebrada: sem isso ela dispararia depois, fora de ordem.
 *  - **o temporizador é um só e é limpo no desmonte.** São quatro dias ligados.
 *
 * @param total      o `vendidoTotal` do painel, ou `null` enquanto nada foi medido
 * @param metaGeral  a meta do cadastro (R$ 28 mi em 13/10)
 */
export function usarComemoracao(total: number | null, metaGeral: number | null): EmCartaz | null {
  const [emCartaz, setEmCartaz] = useState<EmCartaz | null>(null)
  /* Em ref: mudá-los não deve repintar, e são lidos dentro do efeito.
     `undefined` = ainda não inicializado nesta sessão. */
  const ultimo = useRef<number | null | undefined>(undefined)
  const metaJaCelebrada = useRef(false)
  const metadeJaCelebrada = useRef(false)
  const temporizador = useRef<number | null>(null)

  useEffect(() => {
    const armazem = typeof localStorage === 'undefined' ? null : localStorage

    if (ultimo.current === undefined) {
      /* Primeira leitura da sessão. O que estava guardado tem precedência: se o
         telão já comemorou o sexto milhão antes de recarregar, não comemora de
         novo. Sem nada guardado, ancora no total atual — e o próximo milhão é o
         primeiro a ser comemorado. */
      const guardado = lerUltimoMarco(armazem)
      const agora = marcoDe(total)
      ultimo.current = guardado === null ? agora : Math.max(guardado, agora)
      gravarUltimoMarco(armazem, ultimo.current)
      /* A META e a METADE também ancoram: abrir a página com o total já acima
         delas não dispara as festas retroativamente. */
      metaJaCelebrada.current =
        lerMetaCelebrada(armazem) || metaBatida(total, metaGeral, false)
      if (metaJaCelebrada.current) gravarMetaCelebrada(armazem)
      metadeJaCelebrada.current =
        lerMetadeCelebrada(armazem) || metadeBatida(total, metaGeral, false)
      if (metadeJaCelebrada.current) gravarMetadeCelebrada(armazem)
      return
    }

    const marco = marcoACelebrar(total, ultimo.current)
    const meta = metaBatida(total, metaGeral, metaJaCelebrada.current)
    /* A META vence a METADE: numa meta pequena os dois podem cair no mesmo
       instante, e aí o que a sala vê é a meta batida. */
    const metade = !meta && metadeBatida(total, metaGeral, metadeJaCelebrada.current)
    if (marco === null && !meta && !metade) return

    /* Quando a meta ou a metade caem, elas são a comemoração — e o marco do
       milhão vai junto como celebrado, para não render uma segunda festa no
       ciclo seguinte. */
    const marcoEmCartaz = marco ?? marcoDe(total)
    /* `?? 0` pelo tipo: o `undefined` já saiu no `if` acima, mas `ultimo` também
       admite `null` (nunca lido do armazém), e `Math.max(null, n)` é 0 em vez de
       erro — explícito é melhor que silencioso. */
    ultimo.current = Math.max(ultimo.current ?? 0, marcoEmCartaz)
    gravarUltimoMarco(armazem, ultimo.current)
    if (meta) {
      metaJaCelebrada.current = true
      gravarMetaCelebrada(armazem)
      /* Bater a meta implica ter passado da metade: registra as duas, senão a
         festa da metade dispararia depois da da meta, fora de ordem. */
      metadeJaCelebrada.current = true
      gravarMetadeCelebrada(armazem)
    }
    if (metade) {
      metadeJaCelebrada.current = true
      gravarMetadeCelebrada(armazem)
    }
    setEmCartaz({ marco: marcoEmCartaz, meta, metade })

    /* Marco novo durante uma comemoração reinicia a contagem com o número novo:
       o telão mostra sempre o mais alto, e não enfileira quinze segundos por
       milhão. */
    if (temporizador.current !== null) clearTimeout(temporizador.current)
    temporizador.current = setTimeout(() => {
      temporizador.current = null
      setEmCartaz(null)
    }, meta ? DURACAO_META_MS : metade ? DURACAO_METADE_MS : DURACAO_MS) as unknown as number
  }, [total, metaGeral])

  // Desmonte: nenhum temporizador fica pendurado.
  useEffect(() => () => {
    if (temporizador.current !== null) clearTimeout(temporizador.current)
  }, [])

  return emCartaz
}
